import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useAnimate } from 'motion/react'
import { EASE_IN_OUT, EASE_OUT_EXPO } from '@/components/motion/reveal'
import { useLenis } from '@/components/motion/smooth-scroll'
import { Logo } from '@/components/generals/logo'
import { routeLabels } from '@/constants/site'

const labelFor = (pathname: string): string => {
    if (routeLabels[pathname]) return routeLabels[pathname]
    if (pathname.startsWith('/blog/')) return 'Article'
    if (pathname.startsWith('/webinars/')) return 'Webinar'

    return 'nSure.ai'
}

// Two stacked curtains sweep over the page while the next route loads, then lift
// away. Internal link clicks are intercepted so the new page never pops in
// before the screen is covered.
export const PageTransition: FC = (): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()
    const [scope, animate] = useAnimate()
    const [label, setLabel] = useState('')
    const [active, setActive] = useState(false)
    const busy = useRef(false)
    const lenisRef = useRef(lenis)

    lenisRef.current = lenis

    useEffect(() => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const cover = async () => {
            await Promise.all([
                animate('[data-curtain="back"]', { y: ['100%', '0%'] }, { duration: 0.62, ease: EASE_IN_OUT }),
                animate('[data-curtain="front"]', { y: ['100%', '0%'] }, { duration: 0.62, ease: EASE_IN_OUT, delay: 0.07 }),
                animate('[data-curtain-label]', { opacity: [0, 1], y: [30, 0] }, { duration: 0.6, ease: EASE_OUT_EXPO, delay: 0.32 })
            ])
        }

        const reveal = async () => {
            await Promise.all([
                animate('[data-curtain-label]', { opacity: 0, y: -24 }, { duration: 0.35, ease: EASE_IN_OUT }),
                animate('[data-curtain="front"]', { y: '-100%' }, { duration: 0.75, ease: EASE_IN_OUT, delay: 0.12 }),
                animate('[data-curtain="back"]', { y: '-100%' }, { duration: 0.75, ease: EASE_IN_OUT, delay: 0.2 })
            ])

            await Promise.all([
                animate('[data-curtain="front"]', { y: '100%' }, { duration: 0 }),
                animate('[data-curtain="back"]', { y: '100%' }, { duration: 0 })
            ])
        }

        const finish = async () => {
            if (!busy.current) return

            await reveal()
            busy.current = false
            setActive(false)
        }

        const onClick = (event: MouseEvent) => {
            if (reduce || busy.current || event.defaultPrevented || event.button !== 0) return
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

            const anchor = (event.target as HTMLElement | null)?.closest('a')

            if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download') || anchor.dataset.noTransition !== undefined) return

            const href = anchor.getAttribute('href')

            if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return

            const url = new URL(anchor.href, window.location.href)

            if (url.origin !== window.location.origin) return
            if (url.pathname.startsWith('/api/')) return
            if (url.pathname === window.location.pathname && url.search === window.location.search) return

            event.preventDefault()
            busy.current = true
            setActive(true)
            setLabel(labelFor(url.pathname))
            lenisRef.current?.stop()

            void cover().then(() => {
                router.push(url.pathname + url.search + url.hash).catch(() => {
                    void finish()
                })
            })
        }

        const onComplete = () => {
            window.setTimeout(() => {
                void finish()
            }, 120)
        }

        document.addEventListener('click', onClick, true)
        router.events.on('routeChangeComplete', onComplete)
        router.events.on('routeChangeError', onComplete)

        return () => {
            document.removeEventListener('click', onClick, true)
            router.events.off('routeChangeComplete', onComplete)
            router.events.off('routeChangeError', onComplete)
        }
    }, [animate, router])

    return (
        <div
            ref={scope}
            aria-hidden
            className={active ? 'fixed inset-0 z-[95] overflow-hidden' : 'pointer-events-none fixed inset-0 z-[95] overflow-hidden'}
        >
            <div data-curtain='back' className='absolute inset-0 bg-violet' style={{ transform: 'translateY(100%)' }} />

            <div data-curtain='front' className='absolute inset-0 flex flex-col justify-between bg-void p-6 text-mist md:p-10' style={{ transform: 'translateY(100%)' }}>
                <div className='flex items-center justify-between'>
                    <Logo className='h-6 w-auto' />
                    <span className='t-label text-mist/60'>
                        Decision in progress
                    </span>
                </div>

                <div data-curtain-label className='opacity-0'>
                    <span className='t-label mb-4 block text-lavender'>
                        Approved route
                    </span>
                    <span className='block font-heading t-display font-semibold wdth-75 uppercase'>
                        {label}
                    </span>
                </div>

                <div className='flex items-center justify-between t-label text-mist/50'>
                    <span>
                        nSure.ai
                    </span>
                    <span>
                        &lt; 500ms
                    </span>
                </div>
            </div>
        </div>
    )
}
